import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordModal = ({ open, onOpenChange, defaultEmail = "" }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (open) {
      setEmail(defaultEmail);
      setSubmitted(false);
    }
  }, [open, defaultEmail]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.toLowerCase().trim() });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.error("Too many requests. Try again in a minute.");
      } else {
        toast.error(detail || "Couldn't send reset email. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-background border-primary/30" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            FORGOT PASSWORD?
          </DialogTitle>
          <DialogDescription>
            Drop your email and we'll send you a reset link. Valid for 15 minutes.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 text-center space-y-3" data-testid="forgot-password-success">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
            <p className="text-sm text-foreground">
              If an account exists for <strong className="text-primary">{email}</strong>, a reset link has been sent.
            </p>
            <p className="text-xs text-muted-foreground">
              Check your inbox (and spam folder). The link expires in 15 minutes.
            </p>
            <Button onClick={() => onOpenChange(false)} className="mt-4 w-full" data-testid="forgot-password-done-btn">
              Got it
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-2">
            <div>
              <Label htmlFor="forgot-email" className="text-xs uppercase font-bold tracking-wider">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="mt-1"
                data-testid="forgot-password-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !email}
              className="w-full bg-primary hover:bg-primary/90 font-heading text-base h-11"
              data-testid="forgot-password-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SENDING...</>
              ) : (
                "SEND RESET LINK"
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
